// @flow
import React, { type Node, useMemo, useCallback, useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import prop from 'ramda/es/prop';
import clone from 'ramda/es/clone';
import assoc from 'ramda/es/assoc';
import lensProp from 'ramda/es/lensProp';
import over from 'ramda/es/over';
import path from 'ramda/es/path';
import forEachObjIndexed from 'ramda/es/forEachObjIndexed';
import __ from 'ramda/es/__';

import api from 'app/actions/api';
import action from 'app/common/lib/action';

import BannersContext, { defaultBannersState } from './banners-context';
import type { BannerProps } from './banners-types';


const TIMER_INTERVAL = 1000;
const NEXT_BANNER_REQUEST_INTERVAL_DEFAULT = 3600000;

type Props = {
  children: Node,
};

const BannersRoot = ({ children }: Props) => {
  const [banners, setBanners] = useState(defaultBannersState);
  const dispatch = useDispatch();

  const requestBannerData = useCallback(async (placeId: string) => {
    const loading = path([placeId, 'loading'], banners);
    if (loading) return false;
    const requestPath = `promo/banner/${placeId}`;
    const type = 'get';
    try {
      const byPlaceId = lensProp(placeId);
      setBanners(over(byPlaceId, assoc('loading', true)));

      const res = await api(requestPath, type);
      const {
        banner_url: bannerUrl,
        action_url: actionUrl,
        type_id: typeId,
        timing = NEXT_BANNER_REQUEST_INTERVAL_DEFAULT,
      } = prop('data', res) || {};

      const timingTics = timing * 1000;

      setBanners(over(byPlaceId, ({ ...props }) => ({
        ...props,
        placeId, // идентификатор места
        bannerUrl, // картинка баннера
        actionUrl, // ссылка баннера
        typeId, // тип баннера 0 - нет компании, 2 - картинка с ссылкой
        timing: timingTics, // жизнь баннера в ms
        loading: false,
      })));

      // пишем в localStorage. Отсчет существования баннера (или пустоты) пошел, до следующего запроса
      localStorage.setItem(placeId, String(Date.now() + timingTics));
      return true;
    } catch (error) {
      const errorSave = {
        // ошибка не отображается
        // err: t('error_api_banner_data_get'),
        path: requestPath,
        type,
        error: clone(error),
      };
      dispatch(action('REQUEST_FAILURE', errorSave));
      return false;
    }
  }, [dispatch, banners]);


  const isBannerPaused = useCallback((lsData: string) => (
    /^paused_/.test(lsData)
  ), []);

  const getPausedBannerTimeLeft = useCallback((lsData: string) => {
    const left = lsData.replace(/^paused_/, '');
    return parseInt(left, 10) || 0;
  }, []);


  // Истек ли баннер по времени
  // Так же снимается пауза (resume === true)
  const requestBannerTimeActivity = useCallback((placeId: string, fromTimer: ?boolean) => {
    // если нет баннера и запрос не из таймера, то нужно обновление данных (return false)
    if (!fromTimer && !path([placeId, 'typeId'], banners)) {
      return false;
    }
    const lsData = localStorage.getItem(placeId);
    if (!lsData) return false;
    if (isBannerPaused(lsData)) {
      if (!fromTimer) {
        const timeLeft = getPausedBannerTimeLeft(lsData);
        localStorage.setItem(placeId, String(Date.now() + timeLeft));
      }
      return true;
    }
    const currentExpirationTime = parseInt(localStorage.getItem(placeId), 10);
    if (currentExpirationTime && currentExpirationTime > Date.now()) {
      return true;
    }
    return false;
  }, [isBannerPaused, getPausedBannerTimeLeft, banners]);


  const refreshBannerByPlaceId = useCallback((placeId: string, fromTimer: ?boolean) => {
    const bannerTimeActivity = requestBannerTimeActivity(placeId, fromTimer);
    // Запрашиваем данные баннера, если активность окончена или ее еще не было
    if (!bannerTimeActivity) {
      requestBannerData(placeId);
    }
  }, [requestBannerData, requestBannerTimeActivity]);


  const pauseBannerByPlaceId = useCallback((placeId) => {
    const lsData = localStorage.getItem(placeId);
    if (!lsData || isBannerPaused(lsData)) return;
    const currentExpirationTime = parseInt(localStorage.getItem(placeId), 10);
    const nowTics = Date.now();
    if (currentExpirationTime && currentExpirationTime < nowTics) {
      return; // уже истек
    }
    const timeLeft = currentExpirationTime - nowTics;
    localStorage.setItem(placeId, `paused_${timeLeft}`);
  }, [isBannerPaused]);


  // проверка истечения срока баннеров по таймеру
  const handleInterval = useCallback(() => {
    forEachObjIndexed(({ placeId }: BannerProps) => {
      if (!placeId) return; // на всякий случай и для отладки
      refreshBannerByPlaceId(placeId, true);
    }, banners);
  }, [banners, refreshBannerByPlaceId]);


  // выполнение handleInterval по таймеру
  useEffect(() => {
    const intervalId = setInterval(handleInterval, TIMER_INTERVAL);
    return () => {
      clearInterval(intervalId);
    };
  }, [handleInterval]);

  // очистка localStorage
  useEffect(() => () => {
    forEachObjIndexed(({ placeId }: BannerProps) => {
      localStorage.removeItem(placeId);
    }, banners);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps


  const contextValue = useMemo(() => ({
    data: {
      getBannerDataByPlaceId: (prop(__, banners): (placeId: string) => BannerProps | null),
    },
    effects: {
      refreshBannerByPlaceId,
      pauseBannerByPlaceId,
    },
  }), [
    banners,
    refreshBannerByPlaceId,
    pauseBannerByPlaceId,
  ]);


  return (
    <BannersContext.Provider value={contextValue}>
      {children}
    </BannersContext.Provider>
  );
};

export default BannersRoot;
